import React from 'react';
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';

/**
 * Reusable teacher rating display component
 * Shows star rating with count, or "New Teacher" badge if no ratings
 */
export default function TeacherRatingDisplay({
  avgRating = 0,
  ratingsCount = 0,
  isArabic = false,
  size = 'medium', // 'small', 'medium', 'large'
  showCount = true,
  style = {}
}) {
  // Size configurations
  const sizeConfig = {
    small: { starSize: 12, fontSize: 11, badgePadding: '3px 8px', badgeFont: 10 },
    medium: { starSize: 14, fontSize: 12, badgePadding: '4px 10px', badgeFont: 11 },
    large: { starSize: 16, fontSize: 14, badgePadding: '5px 12px', badgeFont: 12 }
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  // If no ratings, show "New" badge
  if (ratingsCount === 0) {
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        ...style
      }}>
        <span style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: '#fff',
          fontSize: config.badgeFont,
          fontWeight: 700,
          padding: config.badgePadding,
          borderRadius: 12,
          fontFamily: "'IBM Plex Sans Arabic', sans-serif"
        }}>
          {isArabic ? 'معلم جديد' : 'New'}
        </span>
      </div>
    );
  }

  // Render stars based on rating
  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(avgRating);
    const hasHalfStar = avgRating % 1 >= 0.3 && avgRating % 1 < 0.8;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`full-${i}`} color="#FFB400" size={config.starSize} />);
    }

    // Half star
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" color="#FFB400" size={config.starSize} />);
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaRegStar key={`empty-${i}`} color="#E5E7EB" size={config.starSize} />);
    }

    return stars;
  };

  const formattedRating = avgRating.toFixed(1);
  const reviewText = isArabic
    ? (ratingsCount === 1 ? 'تقييم واحد' : `${ratingsCount} تقييم`)
    : (ratingsCount === 1 ? '1 review' : `${ratingsCount} reviews`);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      ...style
    }}>
      {/* Stars */}
      <div style={{ display: 'flex', gap: 2 }}>
        {renderStars()}
      </div>

      {/* Rating number */}
      <span style={{
        color: '#64748B',
        fontSize: config.fontSize,
        fontWeight: 600,
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        {formattedRating}
      </span>

      {/* Review count (optional) */}
      {showCount && (
        <span style={{
          color: '#94A3B8',
          fontSize: config.fontSize - 1,
          fontWeight: 500,
          fontFamily: "'IBM Plex Sans Arabic', sans-serif"
        }}>
          ({reviewText})
        </span>
      )}
    </div>
  );
}
